import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { psiTokens } from '../../theme/psiTheme';

const pillars = [
  {
    title: 'Root Cause First',
    description:
      'PSI guides companies beyond symptoms toward the conditions that enabled the problem: structural, operational, or design-related.',
  },
  {
    title: 'Mindset & Methodology',
    description:
      'Improvement is more than tools. PSI develops the thinking patterns that allow teams to solve problems independently.',
  },
  {
    title: 'Sustainable Results',
    description:
      'Solutions designed with PSI are built to last. Teams develop habits to sustain improvements without external dependency.',
  },
];

export default function Philosophy() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box
      sx={{
        backgroundColor: psiTokens.background.primary,
        py: isMobile ? 6 : 8,
      }}
    >
      <Container maxWidth="xl">
        {/* Section header */}
        <Box sx={{ mb: 5 }}>
          {/* Accent line */}
          <Box
            sx={{
              width: '40px',
              height: '3px',
              background: `linear-gradient(90deg, ${psiTokens.primary.dark}, ${psiTokens.primary.light})`,
              borderRadius: '2px',
              mb: 2,
            }}
          />

          <Typography
            sx={{
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: psiTokens.primary.light,
              marginBottom: 2,
            }}
          >
            How PSI Works
          </Typography>

          <Typography
            variant="h2"
            sx={{
              color: psiTokens.text.primary,
              marginBottom: 2,
              fontWeight: 700,
            }}
          >
            Beyond the obvious solution.
          </Typography>

          <Typography
            sx={{
              fontSize: '17px',
              fontWeight: 500,
              color: psiTokens.text.secondary,
              marginBottom: 2,
            }}
          >
            Most organizations treat problems at the surface level. PSI works differently.
          </Typography>

          <Typography
            sx={{
              fontSize: '15px',
              fontWeight: 400,
              color: psiTokens.text.tertiary,
              lineHeight: 1.8,
              maxWidth: '1330px',
            }}
          >
            PSI helps companies develop the capacity to ask the right questions: not just what failed, but what conditions allowed it to
            happen. Through structured methodologies that integrate rational and scientific thinking into the way problems and their root
            causes are analyzed, PSI focuses on an effective transfer of problem-solving knowledge so organizations become self-sufficient.
          </Typography>
        </Box>

        {/* Pillars Grid */}
        <Grid container spacing={3}>
          {pillars.map((pillar, idx) => (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <Card
                sx={{
                  height: '100%',
                  backgroundColor: psiTokens.background.secondary,
                  borderRadius: '12px',
                  borderLeft: `4px solid ${psiTokens.primary.dark}`,
                  border: `1px solid ${psiTokens.border}`,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      color: psiTokens.primary.dark,
                      marginBottom: 2,
                      fontWeight: 700,
                    }}
                  >
                    {pillar.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 400,
                      color: psiTokens.text.secondary,
                      lineHeight: 1.7,
                    }}
                  >
                    {pillar.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
